import {Directive, DoCheck, Input} from '@angular/core';
import {AbstractControl, NG_VALIDATORS, ValidationErrors, Validator} from '@angular/forms';
import {PlanAttachment} from '../../common/models/plan-attachment.model';
import {PlanModelService} from '../../common/service/plan-model.service';

@Directive({
    selector: '[attachmentValidator]',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: AttachmentValidatorDirective,
            multi: true
        }
    ],
    standalone: true
})
export class AttachmentValidatorDirective implements DoCheck, Validator {
    @Input('attachmentValidator')
    attachment: PlanAttachment;

    private onValidatorChange: () => void;

    private attachmentAtLastCheck: string;

    private control: AbstractControl;

    constructor(private planModelService: PlanModelService) {}

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    /**
     * The outcome of this validator depends on the attachment's optionality, which is mutated in place when the applicable
     * attachment configuration is refreshed, and on which attachment occupies this row, which changes when the attachments
     * are re-sorted. Neither moves the bound file location, so nothing would otherwise prompt Angular to re-evaluate the
     * control, leaving it invalid for an attachment that is no longer mandatory.
     *
     * Left alone while the control is disabled, as a disabled control holds no errors to correct and re-evaluating it would
     * still recalculate the whole form it belongs to.
     */
    ngDoCheck(): void {
        if (!this.control || this.control.disabled) {
            return;
        }

        const attachment = `${this.attachment?.attachmentType}:${this.attachment?.optionality}`;
        if (this.attachmentAtLastCheck !== attachment) {
            this.attachmentAtLastCheck = attachment;
            this.onValidatorChange?.();
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this.control = control;

        if (this.hasFileLocationChanged()) {
            control.markAsDirty();
        }

        if (this.attachment.optionality === 'Mandatory') {
            return this.attachment.fileLocation ? null : {attachmentValidator: 'Mandatory'};
        }

        if (this.attachment.optionality === 'Prohibited') {
            return {attachmentValidator: 'Prohibited'};
        }

        return null;
    }

    private hasFileLocationChanged() {
        const pristineModelAttachments = this.planModelService.getPristineModel().attachments || [];

        const pristineModelAttachment = pristineModelAttachments.find(a => a.attachmentType === this.attachment.attachmentType);
        if (pristineModelAttachment) {
            return pristineModelAttachment.fileLocation === this.attachment.fileLocation;
        }

        return false;
    }
}
